import React, { useCallback, useEffect, useRef, useState } from "react";

import { logsRepository } from "repositories";

interface EventLog {
  eventName: string;
  eventType: string;
  testToken: string;
  timestamp: Date;
  extraData?: Record<string, unknown>;
}

interface EventLogQueueContextValues {
  eventQueue: EventLog[];
  isProcessing: boolean;
  trackTestEvent: (
    eventName: string,
    eventType: string,
    testToken: string,
    extraData?: Record<string, unknown>,
  ) => void;
}

export const EventLogQueueContext: React.Context<EventLogQueueContextValues> =
  React.createContext<EventLogQueueContextValues>({
    eventQueue: [],
    isProcessing: false,
    trackTestEvent: () => {},
  });

export const EventLogQueueProvider: React.FC = ({ children }) => {
  const isProcessing = useRef(false);
  const [eventQueue, setEventQueue] = useState<EventLog[]>([]);

  // TODO: reset queue on logout

  // Send event data to logs API
  const submitEventLog = useCallback(async (event: EventLog) => {
    try {
      await logsRepository.trackTestEvent(
        event.eventName,
        event.eventType,
        event.testToken,
        event.timestamp,
        event.extraData,
      );
    } catch (e) {
      console.warn("Failed to register test event!", e);
    }
  }, []);

  // Append new element to the queue
  const enqueueEvent = useCallback((event: EventLog) => {
    setEventQueue((prevQueue) => [...prevQueue, event]);
  }, []);

  // Process first element in queue
  const processNextEvent = useCallback(async () => {
    if (isProcessing.current || eventQueue.length === 0) {
      return;
    }

    isProcessing.current = true;
    const nextEvent = eventQueue[0];
    await submitEventLog(nextEvent);

    // Remove item from queue after it has been processed
    setEventQueue((prevQueue) => prevQueue.slice(1));
    isProcessing.current = false;
  }, [eventQueue, submitEventLog]);

  // Automatically start processing incoming items if nothing is currently in progress
  useEffect(() => {
    if (!isProcessing.current && eventQueue.length > 0) {
      processNextEvent();
    }
  }, [eventQueue, processNextEvent]);

  // Method that will be called from the outside to track events
  // Internally it'd just add the event to the quueue
  const trackTestEvent = useCallback(
    (
      eventName: string,
      eventType: string,
      testToken: string,
      extraData?: Record<string, unknown>,
    ) => {
      const currentDate = new Date();
      enqueueEvent({ eventName, eventType, testToken, timestamp: currentDate, extraData });
    },
    [enqueueEvent],
  );

  return (
    <EventLogQueueContext.Provider
      value={{
        eventQueue,
        isProcessing: isProcessing.current,
        trackTestEvent,
      }}
    >
      {children}
    </EventLogQueueContext.Provider>
  );
};
